import BaseballResultsProcessor from '../entities/BaseballResultsProcessor.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';
import {
    filterOnlyMatchingTagId, filterOnlyMatchingPitchResult, filterOnlyMatchingPitchType,
    filterOnlyMatchingBatterTagId, filterOnlyMatchingPitchSet,
    filterOnlyMatchingBatterHandedness, filterOnlyMatchingPitcherHandedness,
    filterOnlyMatchingStrikeZoneSectors,
    filterOnlyMatchingNumberOfOuts, filterOnlyMatchingNumberOfBalls,
    filterOnlyMatchingNumberOfStrikes, filterOnlyMatchingRunnersOn,
} from '../filters/byParameterValue.js';
import resultPairsSorter from '../sorters/resultPairsSorter.js';
import { PitchReprocessed, ResultData, PitchData, HitData } from '../enums/index.js';

const buildFiltersForPitching = function({
    // tagging context
    pitcherIds, pitchResults, pitchTypes, pitchSet,
    batterIds, batterHandedness, pitcherHandedness,
    zones, attackRegions,
    // scoring context
    outs, balls, strikes, runnersOn,
 }){
    let dataTableFilters = [];

    const taggingFilters = buildTaggingFiltersForPitching({
        pitcherIds, pitchResults, pitchTypes, pitchSet,
        batterIds, batterHandedness, pitcherHandedness,
        zones, attackRegions
    });
    if (taggingFilters instanceof Array){
        dataTableFilters = dataTableFilters.concat(taggingFilters);
    }

    const scoringFilters = buildScoringFiltersForPitching({ outs, balls, strikes, runnersOn });
    if (scoringFilters instanceof Array){
        dataTableFilters = dataTableFilters.concat(scoringFilters);
    }

    // it is necessary to adjust filters, as structure of results collection will be different now
    dataTableFilters.forEach(filter => filter.parameter = ['pitch'].concat(filter.parameter));

    dataTableFilters = [
        // check if there is such a property in Data set and it is not null or undefined or empty string
        { parameter: ['pitch'], operator: 'notEmpty', value: true },
    ].concat(dataTableFilters);

    return dataTableFilters;
};

const buildScoringFiltersForPitching = function({
    outs, balls, strikes, runnersOn,
}){
    let dataTableFilters = [];

    if (outs && outs.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingNumberOfOuts(outs));
    }
    if (balls && balls.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingNumberOfBalls(balls));
    }
    if (strikes && strikes.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingNumberOfStrikes(strikes));
    }
    if (runnersOn && runnersOn.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingRunnersOn(runnersOn));
    }

    return dataTableFilters;
};

const buildTaggingFiltersForPitching = function({
    pitcherIds, pitchResults, pitchTypes, pitchSet,
    batterIds, batterHandedness, pitcherHandedness,
    zones, attackRegions
 }){
    pitchResults = pitchResults || [];
    pitchTypes = pitchTypes || [];
    pitchSet = pitchSet || '';
    batterIds = batterIds || [];
    pitcherIds = pitcherIds || [];
    batterHandedness = batterHandedness || '';
    pitcherHandedness = pitcherHandedness || '';
    zones = zones || [];
    attackRegions = attackRegions || [];
    
    let dataTableFilters = [];

    if (pitchResults && pitchResults.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingPitchResult(pitchResults));
    }
    if (pitchTypes && pitchTypes.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingPitchType(pitchTypes));
    }
    if (batterIds && batterIds.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingBatterTagId(batterIds));
    }
    if (pitcherIds && pitcherIds.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingTagId(pitcherIds));
    }
    if (pitchSet){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingPitchSet(pitchSet));
    }
    if (batterHandedness){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingBatterHandedness(batterHandedness));
    }
    if (pitcherHandedness){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingPitcherHandedness(pitcherHandedness));
    }
    if (zones && zones.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingStrikeZoneSectors(zones));
    }

    // TODO - add attack regions

    return dataTableFilters;
};

const buildFiltersForHitting = function({
    batterIds, batterHandedness, pitcherHandedness
 }){
    batterIds = batterIds || [];
    batterHandedness = batterHandedness || '';
    pitcherHandedness = pitcherHandedness || '';
    
    let dataTableFilters = [];

    if (batterIds && batterIds.length){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingTagId(batterIds));
    }
    if (batterHandedness){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingBatterHandedness(batterHandedness));
    }
    if (pitcherHandedness){
        dataTableFilters = dataTableFilters.concat(filterOnlyMatchingPitcherHandedness(pitcherHandedness));
    }

    // it is necessary to adjust filters, as structure of results collection will be different now
    dataTableFilters.forEach(filter => filter.parameter = ['hit'].concat(filter.parameter));

    dataTableFilters = [
        // check if there is such a property in Data set and it is not null or undefined or empty string
        { parameter: ['hit'], operator: 'notEmpty', value: true },
        { parameter: ['pitch'], operator: 'empty', value: true },
    ].concat(dataTableFilters);

    return dataTableFilters;
};

const getDataTableRows = function(
    results, customResultsProcessor,
    {
        // tagging context
        pitcherIds, pitchResults, pitchTypes, pitchSet,
        batterIds, batterHandedness, pitcherHandedness,
        zones, attackRegions,
        // scoring context
        outs, balls, strikes, runnersOn, //runnersNotOn ?
    } = {}
){
    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    let dataTableFilters = buildFiltersForPitching({
        pitcherIds, pitchResults, pitchTypes, pitchSet,
        batterIds, batterHandedness, pitcherHandedness,
        zones, attackRegions,
        outs, balls, strikes, runnersOn
    });
    
    let dataTableHitsFilters = buildFiltersForHitting({batterIds, batterHandedness, pitcherHandedness});

    let pitchAndHitMatched = BaseballResultsProcessor.matchPitchesAndHits(results);

    let filteredPairs = resultsProcessor.filterData(pitchAndHitMatched, dataTableFilters);
    let filteredHitsOnly = resultsProcessor.filterData(pitchAndHitMatched, dataTableHitsFilters);
    let matchingPairs = filteredPairs.concat(filteredHitsOnly);

    // now we need to sort it using create date...
    matchingPairs.sort(resultPairsSorter);

    return matchingPairs || [];
};

const listUniqueParametersForDataTable = function(
    results, 
    {
        // tagging context
        pitcherIds, pitchResults, pitchTypes, pitchSet,
        batterIds, batterHandedness, pitcherHandedness,
        zones, attackRegions,
        // scoring context
        outs, balls, strikes, runnersOn, //runnersNotOn ?
    } = {},
    pitchParameters, hitParameters, pitchToHitParamsToMerge, customResultsProcessor, combinedPitchResult
){
    pitchParameters = pitchParameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchData.Mechanics.key),
        ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    hitParameters = hitParameters || [
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
        //ResultData.getDataPath(PitchData.Handedness.key), // right now we can't use that
    ];

    pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
        0, 1, //2
    ];

    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();

    let dataTableFilters = buildFiltersForPitching({
        pitcherIds, pitchResults, pitchTypes, pitchSet,
        batterIds, batterHandedness, pitcherHandedness,
        zones, attackRegions,
        outs, balls, strikes, runnersOn
    });

    let dataTableHitsFilters = buildFiltersForHitting({batterIds, batterHandedness, pitcherHandedness});

    let pitchAndHitMatched = BaseballResultsProcessor.matchPitchesAndHits(results);

    let filteredPairs = resultsProcessor.filterData(pitchAndHitMatched, dataTableFilters);
    let filteredHitsOnly = resultsProcessor.filterData(pitchAndHitMatched, dataTableHitsFilters);
    let matchingPairs = filteredPairs.concat(filteredHitsOnly);

    return UniqueParametersForPitchAndHitProvider(
        pitchParameters.map(x => ['pitch'].concat(x)),
        hitParameters.map(x => ['hit'].concat(x)),
        pitchToHitParamsToMerge,
        matchingPairs, // it is ok, hit and pitch filtering is included on parameters level
        matchingPairs,
        resultsProcessor,
        combinedPitchResult
    );
};

export default getDataTableRows;

export {
    getDataTableRows, listUniqueParametersForDataTable
};